import { BackButton, ProductCard } from 'components'
import routes, { formatRoute } from 'config/routes'
import { AppLayout } from 'layouts'
import moment from 'moment-timezone'
import PropTypes from 'prop-types'
import React from 'react'
import { Button, Col, Container, Form, ListGroup, Row } from 'react-bootstrap'
import { connect } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { toPrice } from 'utils'

/**
 * Create a product ID composed of the original ID and the options
 * selected.
 */
const formatProductId = (id, options) => {
  const formattedOptions = Object.keys(options)
    .sort()
    .map((o) => `${o}[${options[o]}]`)
  return [id, ...formattedOptions].join('__')
}

/**
 * Format the product title to include options
 */
const formatProductTitle = (title, allOptions, options) => {
  // debugger
  const formattedOptions = Object.keys(options)
    .sort()
    .map((optFieldCode) => {
      const optField = allOptions.find(
        (optField) => optField.code === optFieldCode
      )
      const optMap = optField.options.reduce((obj, o) => {
        obj[o.code] = o.text
        return obj
      }, {})
      return optMap[options[optFieldCode]]
    })
  return [title, ...formattedOptions].join(', ')
}

export function SelectReward({ appState, fundraiser, onFinish, ...props }) {
  const history = useHistory()
  const { shortcode } = useParams()
  const { first_name, last_name, cart: _cart } = appState
  const rewardClassification = (
    fundraiser.reward_classification || 'reward'
  ).toUpperCase()
  const products = React.useMemo(
    () =>
      moment() >= moment(fundraiser.end_time)
        ? props.products.filter((p) => p.is_digital)
        : props.products,
    [props.products, fundraiser]
  )
  const productIds = React.useMemo(() => products.map((p) => p.id), [products])
  const productMap = React.useMemo(
    () =>
      products.reduce((obj, p) => {
        obj[p.id] = p
        return obj
      }, {}),
    [products]
  )
  const [cart, setCart] = React.useState([
    ..._cart.filter((p) => productIds.includes(p.id)),
  ])
  const isClaraClarkSheetParty = React.useMemo(
    () =>
      products.reduce(
        (isClaraClarkSheetParty, p) =>
          isClaraClarkSheetParty || p.identifier.startsWith('clara-clark-'),
        false
      ),
    [products]
  )

  const addToCart = (productId, quantity, options) => {
    const fullProductId = formatProductId(productId, options)
    const alreadyInCart = cart.some((p) => p.fullId === fullProductId)
    alreadyInCart
      ? setCart((prev) =>
          prev.map((p) =>
            p.fullId === fullProductId
              ? { ...p, quantity: p.quantity + quantity }
              : p
          )
        )
      : setCart((prev) => [
          ...prev,
          {
            id: productId,
            fullId: fullProductId,
            options,
            quantity,
          },
        ])
  }

  const setQuantity = (fullProductId, quantity) => {
    setCart((prev) =>
      prev.map((product) =>
        product.fullId === fullProductId
          ? {
              ...product,
              quantity,
            }
          : product
      )
    )
  }

  const remove = (fullProductId) => {
    setCart((prev) =>
      prev.filter((product) => product.fullId !== fullProductId)
    )
  }

  const checkout = () => {
    onFinish({
      cart,
    })
  }

  return (
    <AppLayout>
      <BackButton
        onClick={() =>
          history.push(formatRoute(routes.getStarted, { shortcode }))
        }
      />
      <Container>
        <Row>
          <Col lg={6} md={10} sm={12} className="mx-auto mx-lg-0">
            <h1>Select Your {rewardClassification}</h1>
            {isClaraClarkSheetParty ? (
              <p className="lead mb-5">
                Thank you for purchasing through{' '}
                <strong className="small heavyset">{fundraiser.title}</strong>.
                Please select your product below.
              </p>
            ) : (
              <p className="lead mb-5">
                Thank you for supporting{' '}
                {!!first_name && last_name && (
                  <>
                    <strong className="small heavyset">{`${first_name} ${last_name}`}</strong>{' '}
                    and{' '}
                  </>
                )}
                <strong className="small heavyset">{fundraiser.title}</strong>.
                Please select the {rewardClassification.toLowerCase()} you would
                like to receive as a thank you for your support.
              </p>
            )}
          </Col>
        </Row>
        <Row className="mb-4 mb-lg-0">
          <Col
            md={12}
            lg={4}
            className="mb-4 mb-lg-0 flex-order-1 flex-order-lg-0"
          >
            <ListGroup className="mb-3" id="sticky-cart">
              <ListGroup.Item>
                <h2 className="h5 m-0">
                  <i className="fas fa-shopping-cart fa-fw mr-1" />
                  Cart
                </h2>
              </ListGroup.Item>
              {cart.length > 0 ? (
                <>
                  {cart.map((product) => (
                    <ListGroup.Item key={product.fullId}>
                      <h6 className="text-transform-none">
                        {formatProductTitle(
                          productMap[product.id].title,
                          productMap[product.id].options,
                          product.options
                        )}
                      </h6>
                      <Row>
                        <Col xs={6}>
                          <Form.Group>
                            <Form.Label className="small text-muted">
                              Quantity
                            </Form.Label>
                            <Form.Control
                              type="number"
                              min="1"
                              value={product.quantity}
                              onChange={(event) =>
                                setQuantity(
                                  product.fullId,
                                  parseInt(event.target.value)
                                )
                              }
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={6} className="text-right">
                          <Form.Label className="small text-muted">
                            Total
                          </Form.Label>
                          <p className="my-1 text-success heavyset">
                            $
                            {toPrice(
                              product.quantity * productMap[product.id].price
                            )}
                          </p>
                        </Col>
                      </Row>
                      <div className="float-right">
                        <Button
                          variant="link"
                          className="small text-danger"
                          size="sm"
                          onClick={() => remove(product.fullId)}
                        >
                          <i className="fas fa-times fa-fw mr-1" />
                          Remove
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </>
              ) : (
                <ListGroup.Item className="text-center">
                  No Selected
                </ListGroup.Item>
              )}
              <ListGroup.Item>
                <Button
                  variant={cart.length === 0 ? 'secondary' : 'primary'}
                  block
                  disabled={cart.length === 0}
                  onClick={checkout}
                >
                  Checkout
                </Button>
              </ListGroup.Item>
            </ListGroup>
          </Col>
          <Col>
            <Row>
              {products.map((product) => (
                <Col
                  key={product.id}
                  md={products.length === 1 ? 8 : 6}
                  className="mb-5 mx-auto mx-lg-0"
                >
                  <ProductCard product={product} onAddToCart={addToCart} />
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      </Container>
    </AppLayout>
  )
}

SelectReward.propTypes = {
  appState: PropTypes.object.isRequired,
  fundraiser: PropTypes.object.isRequired,
  onFinish: PropTypes.func.isRequired,
}

const mapState = (state) => ({
  appState: state.app,
  fundraiser: state.fundraiser,
  products: state.products,
})

export default connect(mapState)(SelectReward)
